import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getStory, listCharacters, listLocations, listProps, generateManifest, getLatestNotification } from "@/lib/api";
import CharacterLockPanel from "@/components/CharacterLockPanel";
import LocationsPropsPanel from "@/components/LocationsPropsPanel";
import RingBadge from "@/components/RingBadge";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Sparkle, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function StoryWorkspace() {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [locations, setLocations] = useState([]);
  const [props, setProps] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [showText, setShowText] = useState(false);

  const load = async () => {
    const [s, c, l, p] = await Promise.all([getStory(storyId), listCharacters(storyId), listLocations(storyId), listProps(storyId)]);
    setStory(s);
    setCharacters(c);
    setLocations(l);
    setProps(p);
    if (s.status === "manifest_review" || s.status === "locked") {
      navigate(`/stories/${storyId}/manifest`, { replace: true });
    } else if (s.status === "manifest_generating") {
      setGenerating(true);
      pollForManifest();
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyId]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await generateManifest(storyId);
      toast.message("Generating manifest…", { description: "Claude Sonnet 4.6 is dividing the story into scenes and shots. This can take up to a minute." });
      pollForManifest();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Manifest generation failed");
      setGenerating(false);
    }
  };

  const pollForManifest = async () => {
    const poll = async () => {
      try {
        const s = await getStory(storyId);
        if (s.status === "manifest_review" || s.status === "locked") {
          const notif = await getLatestNotification(storyId);
          if (notif) toast.message("Telegram ping (stub)", { description: notif.message });
          toast.success("Manifest ready for review");
          navigate(`/stories/${storyId}/manifest`);
          return;
        }
        if (s.status === "draft") {
          toast.error("Manifest generation failed — please retry");
          setGenerating(false);
          return;
        }
        setTimeout(poll, 3000);
      } catch (e) {
        setTimeout(poll, 3000);
      }
    };
    poll();
  };

  if (!story) return <div className="p-6 text-slate-500 text-sm">Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-8" data-testid="story-workspace">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <h1 className="font-heading uppercase tracking-wider text-2xl font-bold text-slate-50">{story.title}</h1>
          <RingBadge ring={story.ring_tag} />
          <StatusBadge status={story.status} />
        </div>
        <button
          data-testid="toggle-story-text-btn"
          onClick={() => setShowText((v) => !v)}
          className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-300 transition-colors"
        >
          <CaretDown size={12} className={`transition-transform ${showText ? "rotate-180" : ""}`} />
          {showText ? "Hide" : "View"} raw story text
        </button>
        {showText && (
          <pre
            data-testid="story-raw-text"
            className="whitespace-pre-wrap font-mono text-xs text-slate-400 bg-slate-900/50 border border-slate-800 rounded-sm p-4 max-h-64 overflow-y-auto"
          >
            {story.raw_text}
          </pre>
        )}
      </div>

      <CharacterLockPanel storyId={storyId} characters={characters} setCharacters={setCharacters} />

      <LocationsPropsPanel storyId={storyId} locations={locations} props={props} setLocations={setLocations} setProps={setProps} />

      <div className="border-t border-slate-800 pt-6 flex items-center justify-between">
        <p className="text-xs text-slate-500 max-w-md">
          LLM pass (Claude Sonnet 4.6) will divide the story into scenes → shots, referencing your locked characters. You can edit/reorder/delete shots after.
        </p>
        <Button
          data-testid="generate-manifest-btn"
          onClick={handleGenerate}
          disabled={generating}
          className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm shrink-0"
        >
          <Sparkle size={16} className="mr-2" />
          {generating ? "Generating Manifest…" : "Generate Scene/Shot Manifest"}
        </Button>
      </div>
    </div>
  );
}
